import React, { useEffect, useState, useContext } from 'react';
import { Grid, IconButton, FormLabel, Typography, CircularProgress, Chip } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import AddBoxIcon from '@material-ui/icons/AddBox';
import DiagnosticModelsModal from './DiagnosticModelsModal';
import DialogComp from './../../../Core/Modal/dialogModal';
import BreadCrumbs from '../../../Core/Controls/Breadcrumb';
import TabComponent from '../../../Core/Controls/Tabs';
import { getDiagnosticModels, postDiagnosticModels, getDiagnosticModelsDropdown } from '../../../Core/Service/diagnosticFaults-service';
import { SecondaryButton } from '../../../Core/FormInput';
import AppContext from './../../../App/AppContext';

const crumbs = () => [
    { name: 'Home', path: '/' },
    { name: 'Diagnostic Sheets', path: '/' },
    { name: 'Diagnostic Models', active: true }
];

const DiagnosticModels = () => {
    const { showToast } = useContext(AppContext);

    const [state, setState] = useState({
        showModal: false,
        id: null, // Model ID being edited/added in the modal
        models: [], // Raw API list of models
        options: [], // Dropdown of Fault options for Multiselect
        tabs: [], // Formatted for TabComponent
        loading: true
    });

    const loadData = async () => {
        setState((st) => ({ ...st, loading: true }));
        try {
            // Load both models and fault dropdown options in parallel
            let [modelsRes, optionsRes] = await Promise.all([getDiagnosticModels(), getDiagnosticModelsDropdown()]);

            // Fix based on API raw data screenshot
            let modelsData = modelsRes?.success ? modelsRes.data?.models || [] : [];
            let optionsData = optionsRes?.success ? optionsRes.data?.list || optionsRes.data?.table || optionsRes.data || [] : [];

            setState((st) => ({
                ...st,
                models: modelsData,
                options: optionsData,
                loading: false
            }));

            formatTabs(modelsData, optionsData);
        } catch (error) {
            console.error('Error loading diagnostic models data', error);
            setState((st) => ({ ...st, loading: false }));
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const formatTabs = (modelsList, faultOptions) => {
        const tabs = modelsList.map((model) => {
            // Faults will be fetched dynamically via TabBodyContent component instead

            return {
                id: model.diagnosticModelID,
                label: <span style={{ display: 'flex', alignItems: 'center' }}>{model.diagnosticModelName}</span>,
                body: <TabBodyContent model={model} options={faultOptions} onEdit={() => handleEdit(model.diagnosticModelID)} />
            };
        });

        // Add the "+" tab
        const AddBtn = {
            id: 'add_new',
            label: <AddBoxIcon color="primary" onClick={() => handleEdit(null)} style={{ cursor: 'pointer', verticalAlign: 'middle' }} />,
            body: (
                <Grid container justifyContent="center" style={{ padding: 40 }}>
                    <Typography color="textSecondary">Click the "+" icon above to create a new Diagnostic Model.</Typography>
                </Grid>
            )
        };

        tabs.push(AddBtn);
        setState((st) => ({ ...st, tabs }));
    };

    const handleEdit = (id) => {
        setState((st) => ({
            ...st,
            showModal: true,
            id: id || null
        }));
    };

    const handleCloseModal = (shouldReload = false) => {
        setState((st) => ({
            ...st,
            showModal: false,
            id: null
        }));
        if (shouldReload) {
            loadData();
        }
    };

    return (
        <div className="report-screen-container" style={{ margin: '0px 10px 0px 5px' }}>
            {state.showModal ? (
                <DialogComp
                    title={`${state.id ? 'Update Diagnostic Model Name' : 'Add Diagnostic Model'}`}
                    maxWidth="md"
                    onClose={() => handleCloseModal(false)}
                    fullWidth
                >
                    <DiagnosticModelsModal onClose={handleCloseModal} id={state.id} />
                </DialogComp>
            ) : null}

            <BreadCrumbs crumbs={crumbs()} />

            <Grid container style={{ marginTop: 20 }}>
                <Grid item xs={12}>
                    {state.loading ? (
                        <Grid container justifyContent="center" style={{ padding: 50 }}>
                            <CircularProgress />
                        </Grid>
                    ) : (
                        <TabComponent tabList={state.tabs} id="diagnostic_models_tabs" selectedIndex={0} />
                    )}
                </Grid>
            </Grid>
        </div>
    );
};

// Extracted internal component to manage state properly per-tab
const TabBodyContent = ({ model, options, onEdit }) => {
    console.log(options, 'options');
    const [faults, setFaults] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const loadTabFaults = async () => {
            setLoading(true);
            try {
                let res = await getDiagnosticModels(model.diagnosticModelID);
                if (isMounted && res.success) {
                    let data = res.data?.models?.[0] || {};

                    // The API returns faultIds alongside models in res.data
                    let rawFaultIds = res.data?.faultIds || data.faultIds || [];
                    console.log(rawFaultIds, 'rawFaultIds');
                    let faultsData = [];
                    if (rawFaultIds.length > 0 || true) {
                        let extractedIds = rawFaultIds.map((f) => (typeof f === 'object' ? f.id : f));
                        console.log(extractedIds, 'extractedIds');
                        faultsData = options.filter((opt) => extractedIds.includes(opt.id) || extractedIds.includes(opt.diagnosticModelID));
                        console.log(faultsData, 'faultsData');
                    }
                    setFaults(faultsData);
                }
            } catch (error) {
                console.error('Error loading specific model data for tab', error);
            }
            if (isMounted) setLoading(false);
        };

        loadTabFaults();

        return () => {
            isMounted = false;
        };
    }, [model.diagnosticModelID, options]);

    return (
        <div style={{ padding: '20px 10px', width: '100%', textAlign: 'left' }}>
            <Grid container spacing={3} justifyContent="flex-start" alignItems="flex-start">
                <Grid item xs={12} sm={11} md={10} lg={8}>
                    <FormLabel
                        component="legend"
                        style={{ marginBottom: 12, fontWeight: 600, color: 'black', textAlign: 'left', display: 'flex', alignItems: 'center' }}
                    >
                        Associated Faults for '{model.diagnosticModelName}'
                        <IconButton
                            size="small"
                            style={{ marginLeft: 10, padding: 2 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onEdit) onEdit();
                            }}
                        >
                            <EditIcon style={{ color: '#f50057', fontSize: 16 }} />
                        </IconButton>
                    </FormLabel>
                </Grid>
                <Grid item xs={12} sm={11} md={10} lg={8}>
                    <div
                        style={{
                            padding: '16px',
                            border: '1px solid rgba(0, 0, 0, 0.15)',
                            borderRadius: 8,
                            minHeight: 50,
                            backgroundColor: '#fbfbfb',
                            textAlign: 'left'
                        }}
                    >
                        {loading ? (
                            <Typography color="textSecondary" variant="body2">
                                Loading...
                            </Typography>
                        ) : faults.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'center' }}>
                                {faults.map((f, i) => (
                                    <Chip
                                        key={i}
                                        label={f.name}
                                        size="small"
                                        style={{
                                            margin: '4px 6px 4px 0',
                                            backgroundColor: '#e0e0e0',
                                            color: '#333',
                                            fontWeight: 500
                                        }}
                                    />
                                ))}
                            </div>
                        ) : (
                            <Typography color="textSecondary" variant="body2" style={{ fontStyle: 'italic' }}>
                                No Associated Faults
                            </Typography>
                        )}
                    </div>
                </Grid>
            </Grid>
        </div>
    );
};

export default DiagnosticModels;
